/**
 * Piezo Client — singleton browser-side Piezo bundle assembler. Provisions the operator key,
 * decks/permits, and Notary/Witness/Plenum clients, and assembles operator-signed artifact bundles.
 * Always returns { ArtifactBundle: <inline base64> }.
 */

import { getTumblerConfig } from '../../../utils/appConfig';
import { PiezoPlenumClient } from '@amzn/piezo-plenum-typescript';
import {
  KeyProvider,
  LocalKeyProvider,
  PiezoNotaryThickClient,
  PiezoOkapiThickClient,
  PiezoWitnessThickClient,
} from '@amzn/piezo-typescript-thick-client';
import { PiezoDebugPrefix, PiezoIndexedDBMetadata, PARSED_CACHE_VERSION } from './PiezoClient.constants';
import { clearPiezoInitFailure, recordPiezoInitFailure, setPiezoInitInProgress } from './initStatus';
import {
  clearMidwaySessionExpired,
  isMidwayAuthError,
  recordMidwaySessionExpired,
} from '../signing/midwaySession';
import {
  PiezoDecks,
  PiezoEncodedNotaryDecks,
  PiezoFetchedDecks,
  PiezoFetchedNotaryDecks,
  PiezoFetchedPermitsMap,
  PiezoFetchedWitnessDeckbox,
  PiezoGetPiezoExecutionContextInput,
  PiezoGetPiezoExecutionContextOutput,
  PiezoIndexedDBSchema,
  PiezoInitInput,
  PiezoInitResult,
  PiezoNotaryDecks,
  PiezoParsedIndexCache,
  PiezoParsedPolicyMetadata,
  PiezoPolicyIndex,
  PiezoPolicyResolution,
  PiezoToolIndex,
  PiezoWitnessDeckbox,
} from './PiezoClient.types';
import {
  buildResourceIndexes,
  cachePiezoResources,
  createActionInfoDoc,
  createArtifactBundle,
  getOperatorRolesFromDecks,
  resolvePolicyForTool,
  selectPermitsForBundle,
  generateKeyPair,
  getDerivedNotaryDecks,
  getDerivedWitnessDeckbox,
  getEncodedDecks,
  getEncodedNotaryDecks,
  hashPermits,
  initPiezoSDKClients,
  loadPiezoResources,
  openPiezoDB,
  shouldRefreshResources,
  slimPolicyIndexForCache,
} from './PiezoClient.utils';
import { executeWitnessWorkflow } from './executionContext';
import { adoptSharedPiezoWasm } from '../wasm/sharedModule';

/**
 * Enforces singleton for PiezoClient across app lifetime
 */
let cachedPiezoClient: PiezoClient | undefined;

/**
 * Returns the singleton instance of PiezoClient, creating one if it doesn't already exist.
 * @returns The shared PiezoClient instance
 */
export function getPiezoClient(): PiezoClient {
  if (!cachedPiezoClient) {
    cachedPiezoClient = new PiezoClient();
  }
  return cachedPiezoClient;
}

/**
 * Client to facilitate Piezo workflow
 * - Communicates with the different external Piezo clients
 * - Handles deck and permit parsing and caching logic
 */
export class PiezoClient {
  /**
   * Client for working with plenum documents (decks, permits, action info doc)
   */
  private plenumClient: PiezoPlenumClient | undefined;
  /**
   * Client for Okapi Issuers which handle identity registration and deck distribution
   */
  private okapiClient: PiezoOkapiThickClient | undefined;
  /**
   * Client for Notary Clients which distribute permits
   */
  private notaryClient: PiezoNotaryThickClient | undefined;
  /**
   * Client for Witness API which creates an observation document (cryptographic proof that the Piezo action was witnessed by an independent party)
   */
  private witnessClient: PiezoWitnessThickClient | undefined;
  /**
   * User's cryptographic key-pair, used for identity registration with Okapi client
   */
  private keyPair: CryptoKeyPair | undefined;
  /**
   * User's key-pair wrapped in the Piezo KeyProvider class used for Piezo operations
   */
  private keyProvider: KeyProvider | undefined;
  /**
   * User's decks, obtained from Okapi client
   */
  private decks: PiezoDecks = [];
  /**
   * User's decks pre-encoded to base64 (mirror of `decks`), derived once at hydrate time so the
   * artifact bundle can reuse the strings instead of re-encoding on every tool call.
   */
  private encodedDecks: string[] = [];
  /**
   * Decks of all Notary Clerks
   */
  private notaryDecks: PiezoNotaryDecks = {};
  /**
   * Notary Clerk decks pre-encoded to base64, keyed by clerk endpoint. Derived once at hydrate time
   * so the artifact bundle can reuse the strings instead of re-encoding on every tool call.
   */
  private encodedNotaryDecks: PiezoEncodedNotaryDecks = {};
  /**
   * Decks from Witness service, keyed by region
   */
  private witnessDeckbox: PiezoWitnessDeckbox = {};
  /**
   * Map of user's policies
   */
  private policyIndex: PiezoPolicyIndex = {};
  /**
   * Map of user's permitted tools and policies associated with that tool
   */
  private toolIndex: PiezoToolIndex = {};
  /**
   * Operator's own POSIX roles (`posix=<group>`), from their deck principal cards at hydrate. Keeps only
   * the policies the operator is a principal for; empty means nothing can be signed.
   */
  private operatorRoles: ReadonlySet<string> = new Set();
  /**
   * Piezo IndexedDB object
   */
  private db: IDBDatabase | undefined;
  /**
   * AWS deployment region used to configure Piezo SDK clients
   */
  private region: string | undefined;

  /**
   * In-flight or completed init promise, so repeat/concurrent init() calls share one run
   * instead of racing or re-fetching resources.
   */
  private initPromise?: Promise<PiezoInitResult>;

  /**
   * Initializes the Piezo client. Idempotent: repeat/concurrent callers (the post-auth init in
   * index.tsx and per-request signing in piezoLink) share a single in-flight init instead of
   * racing or re-fetching resources. A failed init is not cached, so a later call retries.
   * @returns Promise resolving to initialization result with permitted tools or error message
   */
  /** Region the SDK clients were built for; lets ensureClients() detect a region change. */
  private clientsRegion?: string;

  public init(input: PiezoInitInput): Promise<PiezoInitResult> {
    const forceRefresh = input.loadFromCache === false;
    if (this.initPromise && !forceRefresh) {
      return this.initPromise;
    }
    // Past the return above there are only two cases: nothing is running, or this is a forced refresh that
    // has to queue BEHIND the run in flight -- two concurrent runs would double the network fetch and
    // interleave their IndexedDB writes. The catch swallows the previous run's failure; this run's result
    // is its own.
    const previousRun = this.initPromise?.catch(() => undefined) ?? Promise.resolve();
    const p = previousRun
      .then(() => this.runInit(input))
      .then((res) => {
        if ('error' in res && this.initPromise === p) {
          this.initPromise = undefined;
        }
        return res;
      });
    this.initPromise = p;
    return p;
  }

  /**
   * Builds the Piezo SDK clients once and reuses them, rebuilding only when a client is missing or
   * the region changed. Replaces four copies of the init block that each used a different (and
   * inconsistent) presence guard.
   */
  private async ensureClients(): Promise<void> {
    if (!this.region) {
      throw new Error('PiezoClient not initialized: missing region. Call init({ region }) first.');
    }
    if (
      this.plenumClient &&
      this.okapiClient &&
      this.notaryClient &&
      this.witnessClient &&
      this.clientsRegion === this.region
    ) {
      return;
    }
    // Must come first. The call below builds the Plenum client, and that compiles the WASM binary itself
    // unless one is already loaded. Handing it the copy we compiled earlier turns a second ~20s compile
    // into a no-op.
    await adoptSharedPiezoWasm();
    const { plenumClient, okapiClient, notaryClient, witnessClient } = await initPiezoSDKClients(
      this.region,
    );
    this.plenumClient = plenumClient;
    this.okapiClient = okapiClient;
    this.notaryClient = notaryClient;
    this.witnessClient = witnessClient;
    this.clientsRegion = this.region;
  }

  /** Returns { error } rather than throwing: init() memoises this promise, so a rejection would stick. */
  private async runInit({
    region,
    loadFromCache = true,
    cacheOnly = false,
  }: PiezoInitInput): Promise<PiezoInitResult> {
    try {
      // Store region for use in subsequent SDK client calls
      this.region = region;

      this.db = this.db || (await openPiezoDB());

      await this.ensureClients();

      const cachedResources: PiezoIndexedDBSchema | undefined = loadFromCache
        ? await loadPiezoResources(this.db)
        : undefined;

      // If available, use cached Piezo resources
      if (cachedResources) {
        await this.hydrateLocalResources(cachedResources, 'cache', cacheOnly);

        // If cached resources are older than 3 days, refresh in the background and use loaded resources until refresh is complete
        if (!cacheOnly && shouldRefreshResources(cachedResources.lastModifiedDate)) {
          // Needed by BOTH calls: the write is a full-record `put`, so omitting it deletes a valid index,
          // and hydrate decides whether to re-parse from the object it is handed.
          const knownParsedIndex = cachedResources.parsedIndex;
          this.refreshResources(knownParsedIndex)
            .then(async (freshResources: PiezoIndexedDBSchema) => {
              clearMidwaySessionExpired();
              await this.hydrateLocalResources(
                { ...freshResources, parsedIndex: knownParsedIndex },
                'sdk',
              );
            })
            .catch((err) => {
              if (isMidwayAuthError(err)) {
                recordMidwaySessionExpired();
              }
              console.error(`${PiezoDebugPrefix} Resource refresh failed: ${String(err)}`);
            });
        }

        clearPiezoInitFailure();
        return {
          success: {
            data: {
              lastInitDate: cachedResources.lastModifiedDate,
              policyIndex: this.policyIndex,
              toolIndex: this.toolIndex,
            },
          },
        };
      }

      // Workers pass cacheOnly and can only read. Refuse instead of fetching, so N workers don't each
      // hit the network and race on IndexedDB writes; the caller signs on the main thread instead.
      if (cacheOnly) {
        return {
          error: {
            message: 'Piezo cacheOnly init: cached resources not available; not fetching from network',
          },
        };
      }

      setPiezoInitInProgress(true);
      // The refetch writes the whole record, so without carrying a known index through it the index is
      // gone for the length of the fetch and any worker starting then re-parses every permit.
      const preservedParsedIndex = loadFromCache
        ? undefined
        : (await loadPiezoResources(this.db).catch(() => undefined))?.parsedIndex;
      const freshResources = await this.refreshResources(preservedParsedIndex);
      await this.hydrateLocalResources(freshResources, 'sdk');
      clearMidwaySessionExpired();
      clearPiezoInitFailure();

      return {
        success: {
          data: {
            lastInitDate: freshResources.lastModifiedDate,
            policyIndex: this.policyIndex,
            toolIndex: this.toolIndex,
          },
        },
      };
    } catch (err) {
      // Recorded here rather than in each caller, since init returns its error instead of throwing.
      if (isMidwayAuthError(err)) {
        recordMidwaySessionExpired();
      }
      const message =
        err instanceof Error ? `${err.name}: ${err.message}` : 'Error initializing Piezo';
      recordPiezoInitFailure(message);
      console.error(`${PiezoDebugPrefix} Piezo init failed:`, err);
      return { error: { message } };
    } finally {
      setPiezoInitInProgress(false);
    }
  }

  /**
   * Fetches a new set of Piezo resources and caches them in IndexedDB.
   *
   * `preservedParsedIndex` is carried into the write below because cachePiezoResources does a `put`, a
   * full-record replace. Omitting it DELETES a valid parsed index until hydrate rewrites it, and a worker
   * reading the cache in that window re-parses every permit. Undefined is correct only on the cold path.
   */
  private async refreshResources(
    preservedParsedIndex?: PiezoParsedIndexCache,
  ): Promise<PiezoIndexedDBSchema> {
    await this.ensureClients();

    const keyPair: CryptoKeyPair = this.keyPair ?? (await generateKeyPair());
    const keyPairProvider: LocalKeyProvider = new LocalKeyProvider(crypto.subtle, keyPair);

    const fetchedDecks: PiezoFetchedDecks = await this.okapiClient.registerIdentity(keyPairProvider);
    if (fetchedDecks.length === 0) {
      throw Error('No decks were returned by the Okapi client upon key-pair registration');
    }

    const fetchedNotaryDecks: PiezoFetchedNotaryDecks = await this.notaryClient.listAllClerkDecks();
    if (fetchedNotaryDecks.size === 0) {
      throw Error('No Notary Clerk decks were returned by the Notary client');
    }

    const fetchedWitnessDeckbox: PiezoFetchedWitnessDeckbox = await this.witnessClient.listIdentityDocuments();

    const fetchedPermits: PiezoFetchedPermitsMap = await this.notaryClient.listAllPermits();

    const piezoResourceSet: PiezoIndexedDBSchema = {
      id: PiezoIndexedDBMetadata.table.keyValue,
      lastModifiedDate: new Date(),
      keyPair,
      fetchedDecks,
      fetchedNotaryDecks,
      fetchedPermits,
      fetchedWitnessDeckbox,
      parsedIndex: preservedParsedIndex,
    };

    if (!this.db) {
      this.db = await openPiezoDB();
    }
    await cachePiezoResources(this.db, piezoResourceSet);

    return piezoResourceSet;
  }

  /**
   * Hydrates class members with the provided Piezo resources.
   *
   * Derives into locals and assigns `this` in one block at the end: the index build in between can throw,
   * and assigning as it went left the client with some members updated and others stale.
   */
  private async hydrateLocalResources(
    resources: PiezoIndexedDBSchema,
    source: 'cache' | 'sdk',
    readOnly = false,
  ): Promise<void> {
    if (!resources.keyPair) {
      throw Error(`Missing key pair when hydrating local resources from ${source}`);
    }
    const keyPair = resources.keyPair;

    const keyProvider = new LocalKeyProvider(crypto.subtle, resources.keyPair);
    if (!keyProvider) {
      throw Error(`Failed to create key provider when hydrating local resources from ${source}`);
    }

    // Build derived objects from fetched Piezo resources
    if (resources.fetchedDecks.length === 0) {
      throw Error(`No decks when hydrating local resources from ${source}`);
    }
    const decks = resources.fetchedDecks;
    const encodedDecks = getEncodedDecks(resources.fetchedDecks);

    if (resources.fetchedNotaryDecks.size === 0) {
      throw Error(`No Notary Clerk decks when hydrating local resources from ${source}`);
    }
    const notaryDecks = getDerivedNotaryDecks(resources.fetchedNotaryDecks);
    const encodedNotaryDecks = getEncodedNotaryDecks(notaryDecks);

    if (resources.fetchedWitnessDeckbox.size === 0) {
      throw Error(`No Witness decks when hydrating local resources from ${source}`);
    }
    const witnessDeckbox = getDerivedWitnessDeckbox(resources.fetchedWitnessDeckbox);

    await this.ensureClients();

    // Build (or reuse cached) policy/tool indexes. Parsing every permit via Plenum WASM is the most
    // expensive part of hydrate, so we cache the derived indexes in IndexedDB keyed by a hash of the
    // raw permits plus a schema version, and skip re-parsing when they still match. See
    // PIEZO_OPT1_CACHE_LIFECYCLE.
    const permitsHash = await hashPermits(resources.fetchedPermits);
    const cachedParsed = resources.parsedIndex;
    let policyIndex: PiezoPolicyIndex;
    let toolIndex: PiezoToolIndex;
    let operatorRoleSet: Set<string>;
    if (
      cachedParsed &&
      cachedParsed.version === PARSED_CACHE_VERSION &&
      cachedParsed.permitsHash === permitsHash
    ) {
      policyIndex = cachedParsed.policyIndex;
      toolIndex = cachedParsed.toolIndex;
      // Absent OR empty means recompute, never trust: resolution fails closed, so an empty cached role
      // set would silently stop every tool from signing.
      const cachedRoles = cachedParsed.operatorRoles;
      operatorRoleSet = new Set(
        cachedRoles?.length
          ? cachedRoles
          : await getOperatorRolesFromDecks(encodedDecks, this.plenumClient),
      );
    } else {
      const built = await buildResourceIndexes(resources.fetchedPermits, this.plenumClient);
      policyIndex = built.policyIndex;
      toolIndex = built.toolIndex;
      const operatorRoles = await getOperatorRolesFromDecks(encodedDecks, this.plenumClient);
      operatorRoleSet = new Set(operatorRoles);

      // Save the parsed index so later loads skip re-parsing. Skipped for worker hydrations: only the
      // main thread writes. Awaited so workers hydrating after init() find it instead of re-parsing.
      if (this.db && !readOnly) {
        const parsedIndex = {
          version: PARSED_CACHE_VERSION,
          permitsHash,
          policyIndex: slimPolicyIndexForCache(policyIndex),
          toolIndex,
          operatorRoles,
        };
        try {
          await cachePiezoResources(this.db, { ...resources, parsedIndex });
        } catch (err) {
          console.error(`${PiezoDebugPrefix} Failed to backfill parsed index cache: ${String(err)}`);
        }
      }
    }

    // The one visible commit -- nothing above touches `this`.
    this.keyPair = keyPair;
    this.keyProvider = keyProvider;
    this.decks = decks;
    this.encodedDecks = encodedDecks;
    this.notaryDecks = notaryDecks;
    this.encodedNotaryDecks = encodedNotaryDecks;
    this.witnessDeckbox = witnessDeckbox;
    this.policyIndex = policyIndex;
    this.toolIndex = toolIndex;
    this.operatorRoles = operatorRoleSet;

    // Eligibility summary for BOTH index branches: a large `policies` with eligible=0 is the signature
    // of a role-extraction problem.
    const eligiblePolicyCount = Object.values(policyIndex).filter((policy) =>
      operatorRoleSet.has(policy.principal),
    ).length;
    if (operatorRoleSet.size === 0) {
      console.error(
        `${PiezoDebugPrefix} hydrate: NO operator POSIX roles resolved from ${decks.length} ` +
          `deck(s) — every policy is ineligible, so NOTHING can be signed. Likely a deck principal tag ` +
          `key casing change (see getDeckPrincipalTags) or too few distinct issuer decks.`,
      );
    } else if (eligiblePolicyCount === 0) {
      console.error(
        `${PiezoDebugPrefix} hydrate: ${operatorRoleSet.size} role(s) resolved but none of the ` +
          `${Object.keys(policyIndex).length} indexed policies has a matching principal, so ` +
          `NOTHING can be signed. Policy principals seen: ` +
          `${JSON.stringify(Array.from(new Set(Object.values(policyIndex).map((policy) => policy.principal))))}`,
      );
    }
  }

  /**
   * Creates the Piezo execution context object for StartExecution. Always returns
   * { ArtifactBundle: string } inline.
   */
  /**
   * Resolves the policy index key for a tool from the operator's OWN eligible policies: a policy
   * qualifies only when its `principal` is one of the operator's POSIX roles. Callers MUST NOT sign on
   * failure. Returns the composite `policyHash_policyName` policyIndex is keyed by, not a bare hash.
   */
  public resolvePolicyForTool(toolId: string): PiezoPolicyResolution {
    const resolution = resolvePolicyForTool({
      toolId,
      toolIndex: this.toolIndex,
      policyIndex: this.policyIndex,
      operatorRoles: this.operatorRoles,
      // Read here, not stored at init(): init takes a region, not a stage, and a second copy of a value
      // the config already owns is a second thing that can go stale.
      stage: getTumblerConfig()?.stage,
    });

    // `=== true`, not truthiness: strictNullChecks is off, so only equality narrows the union.
    if (resolution.ok === true) {
      return resolution;
    }

    const detail =
      resolution.failure === 'TOOL_NOT_FOUND'
        ? `tool not present in toolIndex (${Object.keys(this.toolIndex).length} tool(s) indexed)`
        : `none of ${resolution.candidates.length} candidate policy(ies) has a principal in the ` +
          `operator's roles ${JSON.stringify(Array.from(this.operatorRoles))}; candidate principals: ` +
          `${JSON.stringify(resolution.candidates.map((key) => this.policyIndex[key]?.principal))}`;
    console.error(
      `${PiezoDebugPrefix} resolvePolicyForTool: ${resolution.failure} for "${toolId}" — ${detail}. ` +
        `This tool call cannot be signed.`,
    );
    return resolution;
  }

  public async getPiezoExecutionContext(
    inputs: PiezoGetPiezoExecutionContextInput,
  ): Promise<PiezoGetPiezoExecutionContextOutput> {

    await this.ensureClients();

    if (!this.keyProvider) {
      throw new Error('PiezoClient not initialized: missing key pair. Call init() first.');
    }

    let actionInfo: string | undefined;
    let additionalDecks: string[] = [];

    const resolverContext = {
      keyProvider: this.keyProvider,
      plenumClient: this.plenumClient,
      okapiClient: this.okapiClient,
      decks: this.decks,
      encodedDecks: this.encodedDecks,
      notaryDecks: this.notaryDecks,
      policyIndex: this.policyIndex,
    };

    const selectedPolicy: PiezoParsedPolicyMetadata = this.policyIndex[inputs.policyHash];
    if (!selectedPolicy) {
      throw Error(`Selected policy with hash ${inputs.policyHash} does not exist`);
    }

    // Create the action info doc (base64-encoded string)
    actionInfo = await createActionInfoDoc({ inputs, resolverContext });

    // Pick the minimal permits for this action. Fails open to all of them on any anomaly.
    const selectedPermits = await selectPermitsForBundle({
      actionInfo: actionInfo ?? '',
      permits: selectedPolicy.permits,
      plenumClient: this.plenumClient,
    });
    // Include all notary clerk decks (pre-encoded at hydrate). Endpoint-filtering is unsafe: it can
    // drop a clerk deck needed to authenticate an embedded permit.
    for (const encodedDecks of Object.values(this.encodedNotaryDecks)) {
      additionalDecks.push(...encodedDecks);
    }

    // Witness Workflow — mandatory for V2 protocol
    let observationDocuments: string[] | undefined;
    if (!inputs.disableWitness) {
      const witnessWorkflowResult = await executeWitnessWorkflow({
        actionInfo,
        witnessClient: this.witnessClient,
        plenumClient: this.plenumClient,
        witnessDeckbox: this.witnessDeckbox,
      });

      actionInfo = witnessWorkflowResult.observedActionInfo;
      observationDocuments = witnessWorkflowResult.observationDocuments;
      // Include all witness decks for the region (the observation author's deck must be present).
      additionalDecks.push(...witnessWorkflowResult.observationDeckbox);
    }

    // Create the artifact bundle (base64-encoded string)
    const artifactBundle: string = await createArtifactBundle({
      actionInfo,
      additionalDecks,
      observationDocuments,
      selectedPermits,
      inputs,
      resolverContext,
    });

    // Always return the inline ArtifactBundle.
    return { ArtifactBundle: artifactBundle };
  }
}
